import { IElement } from "core-integration/src/store/elements/types";
import ElementDetailsBar from "plos/src/components/ElementCard/ElementDetailsBar";
import { ElementIcon } from "plos/src/components/tooltips/ElementIcon";
import LockedIcon from "plos/src/img/icons/locked.svg?react";
import {
  getDifficultyColor,
  getDifficultyTextColor,
} from "plos/src/styles/difficultyColors";
import { visuallyHidden } from "plos/src/styles/utils.css";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { getPriceChangeStatus } from "../../../PriceChanges/utils";
import styles from "./priceChangeOverlay.module.css";

interface PriceChangeOverlayProps {
  element: IElement;
}

const PriceChangeOverlay = ({ element }: PriceChangeOverlayProps) => {
  const { t } = useTranslation("common");

  const priceChangePercent = Number(element.price_change_percent);
  const absProgress = Math.abs(priceChangePercent);
  const rating = getPriceChangeStatus(element);

  // Bar is capped at ±100; beyond that the exact value only shows in the label/aria-valuetext
  const progressValue = Math.max(-100, Math.min(100, priceChangePercent));
  const fillWidth = Math.min(absProgress, 100);
  const displayProgress = `${
    priceChangePercent > 0 ? "+" : ""
  }${priceChangePercent}%`;

  return element.price_change_locked_until ? (
    <ElementDetailsBar>
      <ElementIcon
        label={t(
          "pitchControls.pitchOverlay.priceChangeLocked",
          "Price change locked",
        )}
      >
        <LockedIcon className={styles.lockedIcon} />
      </ElementIcon>
    </ElementDetailsBar>
  ) : (
    <ElementDetailsBar>
      <span className={visuallyHidden}>
        {t("pitchControls.pitchOverlay.priceChange", "Price change")}
      </span>
      <div
        className={styles.priceChangeProgressWrapper}
        role="progressbar"
        aria-valuenow={progressValue}
        aria-valuemin={-100}
        aria-valuemax={100}
        aria-valuetext={displayProgress}
        aria-label={t(
          "pitchControls.pitchOverlay.priceChangeLabel",
          "Price change progress",
        )}
      >
        <div
          className={`${styles.priceChangeProgressFill} ${getDifficultyColor(
            rating,
          )}`}
          style={{ "--fill-width": `${fillWidth}%` } as CSSProperties}
        />
        <span
          className={`${
            styles.priceChangeProgressLabel
          } ${getDifficultyTextColor(rating)}`}
        >
          {displayProgress}
        </span>
      </div>
    </ElementDetailsBar>
  );
};

export default PriceChangeOverlay;
